// theme.ts

import { createTheme } from "@mui/material/styles";

// Common design tokens used across both themes
const designTokens = {
  typography: {
    fontFamily: ["-apple-system", "BlinkMacSystemFont", '"Segoe UI"', "Roboto", '"Helvetica Neue"', "Arial", "sans-serif"].join(
      ",",
    ),
    h1: { fontSize: "2.5rem", fontWeight: 700, letterSpacing: "-0.02em" },
    h2: { fontSize: "2rem", fontWeight: 700, letterSpacing: "-0.02em" },
    h3: { fontSize: "1.75rem", fontWeight: 600, letterSpacing: "-0.01em" },
    h4: { fontSize: "1.5rem", fontWeight: 600, letterSpacing: "-0.01em" },
    h5: { fontSize: "1.25rem", fontWeight: 600 },
    h6: { fontSize: "1rem", fontWeight: 600 },
    body1: { fontSize: "1rem", lineHeight: 1.6 },
    body2: { fontSize: "0.875rem", lineHeight: 1.6 },
    button: { textTransform: "none" as const, fontWeight: 600 },
  },
  shape: {
    borderRadius: 8, // Modern, clean aesthetics
  },
};

// Light Palette Options (Inherited professionally from #80BFFF and #FF99C8)
const lightPalette = {
  mode: "light" as const,
  primary: {
    main: "#0077cc", // Professional deeper corporate adaptation of #80BFFF
    light: "#80BFFF", // Your exact base blue as light variant
    dark: "#005299",
    contrastText: "#ffffff",
  },
  secondary: {
    main: "#e66b9d", // Professional deep adaptation of #FF99C8
    light: "#FF99C8", // Your exact base pink as light variant
    dark: "#b84775",
    contrastText: "#ffffff",
  },
  background: {
    default: "#f4f8fc", // Cool airy blue-tinted slate background
    paper: "#ffffff",
  },
  text: {
    primary: "#0f172a", // Slate 900
    secondary: "#475569", // Slate 600
  },
  divider: "rgba(15, 23, 42, 0.08)",
};

// Dark Palette Options (Leveraging your bright base colors for stunning dark-mode contrast)
const darkPalette = {
  mode: "dark" as const,
  primary: {
    main: "#80BFFF", // Your exact primary base color shines wonderfully on dark backgrounds
    light: "#b3d9ff",
    dark: "#4da6ff",
    contrastText: "#0f172a",
  },
  secondary: {
    main: "#FF99C8", // Your exact secondary base color used as a vibrant, professional accent
    light: "#ffc2de",
    dark: "#ff70b3",
    contrastText: "#0f172a",
  },
  background: {
    default: "#0b131a", // Deep professional navy-slate shade
    paper: "#131f2b", // Elevated surface card shade
  },
  text: {
    primary: "#f8fafc", // Slate 50
    secondary: "#94a3b8", // Slate 400
  },
  divider: "rgba(255, 255, 255, 0.08)",
};

// Reusable component overrides to keep layout styles uniform
const createComponentOverrides = (mode: "light" | "dark") => ({
  components: {
    MuiButton: {
      defaultProps: {
        disableElevation: true, // Flat, cleaner visual interface
      },
      styleOverrides: {
        root: {
          borderRadius: "50px", // Rounded capsule shape buttons
          padding: "8px 20px",
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: "none", // Eradicates default MUI gray opacity shifts in dark mode
          boxShadow:
            mode === "light"
              ? "0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)"
              : "0 4px 6px -1px rgb(0 0 0 / 0.3), 0 2px 4px -2px rgb(0 0 0 / 0.3)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          border: mode === "light" ? "1px solid rgba(15, 23, 42, 0.06)" : "1px solid rgba(255, 255, 255, 0.06)",
        },
      },
    },
  },
});

// Exportable generating factory functions
export const getLightTheme = () => {
  return createTheme({
    ...designTokens,
    palette: lightPalette,
    ...createComponentOverrides("light"),
  });
};

export const getDarkTheme = () => {
  return createTheme({
    ...designTokens,
    palette: darkPalette,
    ...createComponentOverrides("dark"),
  });
};
